import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackChallenge } from "@/lib/ga4";

const REASON_TEXT = {
  not_open: "Die Challenge ist noch nicht geöffnet.",
  closed: "Die Anmeldung ist gerade geschlossen.",
  paused: "Die Challenge ist gerade pausiert.",
  ended: "Diese Challenge ist beendet.",
  malformed: "Dieser Link ist nicht gültig. Bitte nutze den Link aus deiner Mail.",
  invalid_signature: "Dieser Link ist nicht gültig. Bitte nutze den Link aus deiner Mail.",
  expired: "Dieser Link ist abgelaufen.",
};

const Shell = ({ children, state }) => (
  <div className="min-h-screen bg-[#FFF9F2] text-slate-900 flex items-center justify-center px-5 py-16" data-testid="challenge-join-page" data-token-state={state}>
    <div className="w-full max-w-xl">{children}</div>
  </div>
);

// Challenge v2: kostenloser Einstieg ueber den personalisierten KlickTipp-Link (?t=<E-Mail>&fn=<Vorname>).
export default function ChallengeJoinV2() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const fn = params.get("fn") || "";
  const [st, setSt] = useState({ status: "checking" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setSt({ status: "invalid", reason: "malformed" }); return; }
    api.get(`/challenge2/join/verify?t=${encodeURIComponent(token)}&fn=${encodeURIComponent(fn)}`)
      .then(({ data }) => {
        trackChallenge("link_opened", { link_ok: !!data.ok || !!data.already_member });
        if (data.ok || data.already_member) setSt({ status: "valid", ...data });
        else setSt({ status: "invalid", reason: data.reason, join: data.join });
      })
      .catch(() => setSt({ status: "invalid", reason: "error" }));
  }, [token, fn]);

  const join = async () => {
    if (!consent || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/challenge2/join/activate", { token, consent: true, fn: fn || undefined });
      trackChallenge("join_completed", { already_member: data.already_member });
      window.location.href = data.redirect_to || "/challenge/tag/1";
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(REASON_TEXT[d?.error] || "Einstieg fehlgeschlagen. Bitte erneut versuchen.");
      setBusy(false);
    }
  };

  if (st.status === "checking") {
    return <Shell state="checking"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></Shell>;
  }
  if (st.status === "invalid") {
    return (
      <Shell state="invalid">
        <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="challenge-join-invalid">
          <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-lg font-bold" data-testid="challenge-join-invalid-reason">{REASON_TEXT[st.reason] || "Dieser Link funktioniert gerade nicht."}</p>
        </div>
      </Shell>
    );
  }

  const name = st.first_name || "";
  return (
    <Shell state="valid">
      <div className="rounded-2xl bg-[#0F172A] text-white p-8 sm:p-10 shadow-xl">
        <p className="text-xs font-bold uppercase tracking-widest text-white/60">{name ? `Hallo ${name}` : "Willkommen"}</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold leading-tight" data-testid="challenge-join-headline">{st.join?.headline}</h1>
        <p className="mt-4 text-base text-white/80 leading-relaxed whitespace-pre-line" data-testid="challenge-join-text">{st.join?.text}</p>
        {st.already_member ? (
          <p className="mt-6 inline-flex items-center gap-2 text-emerald-300 text-sm" data-testid="challenge-join-already"><CheckCircle2 className="w-4 h-4" /> Du bist schon dabei.</p>
        ) : null}
        <label className="mt-8 flex items-start gap-3 text-sm text-white/80 cursor-pointer" data-testid="challenge-join-consent-label">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#F3651D]" data-testid="challenge-join-consent" />
          <span>Ich möchte an der Challenge teilnehmen. Dafür wird mit {st.email_masked} ein kostenloses Konto angelegt bzw. mein bestehendes Konto genutzt.</span>
        </label>
        <button type="button" onClick={join} disabled={!consent || busy} data-testid="challenge-join-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-40 text-white font-extrabold px-6 py-4 transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{st.already_member ? "Weiter zur Challenge" : st.join?.cta_label} <ArrowRight className="w-5 h-5" /></>}
        </button>
        {error && <p className="mt-3 text-sm text-red-300" data-testid="challenge-join-error">{error}</p>}
      </div>
    </Shell>
  );
}
